import { Box, Button, Card, CardHeader, CircularProgress, Container, IconButton, MenuItem, Stack, TextField, Typography, alpha } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useDropzone } from "react-dropzone";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { RentalOrganizationSettings } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import useModelSavingData from "src/hooks/use-model-saving-data";
import { usePrevious } from "src/hooks/use-previous";
import Iconify from "src/components/iconify/iconify";
import TruxTextField from "src/components/inputs/trux-text-field";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import TruxSelectField from "src/components/inputs/trux-select-field";
import { Constants, RENTAL_TIRE_CONDITION_TYPES } from "src/constants";
import SignatureCanvas from "react-signature-canvas";
import { RentalFileField } from "../trailers/rental-file-field";
import { uploadSignatureCanvas } from "../trailers/rental-trailer-event-form";

type PageModel = {
  rentalOrganizationSettings?: RentalOrganizationSettings,
}

export default function Page() {
  const settings = useSettingsContext();

  const dataLoader = useCallback(() => ({
    rentalOrganizationSettings: axios.get(endpoints.rentalOrganizationSettings.current).then(response => response.data),
  }), []);

  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);

  const previousRentalOrganizationSettings = usePrevious(data?.rentalOrganizationSettings)

  const rentalOrganizationSettingsModel = useModelSavingData<RentalOrganizationSettings>(
    endpoints.rentalOrganizationSettings.update,
    endpoints.rentalOrganizationSettings.update
  );

  useEffect(() => {
    if (data?.rentalOrganizationSettings && data.rentalOrganizationSettings !== previousRentalOrganizationSettings) {
      rentalOrganizationSettingsModel.set(data.rentalOrganizationSettings)
    }
  }, [previousRentalOrganizationSettings, data?.rentalOrganizationSettings, rentalOrganizationSettingsModel]);



  const [savingFile, setSavingFile] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(file => {
        setSavingFile(true)

        // here we need to upload the image
        axios.postForm(
          endpoints.organizations.uploadFile,
          { file }
        ).then(response => response.data).then((response) => {
          if (response.fileUrl) {
            enqueueSnackbar("Successfully uploaded the File", { variant: "success" });

            rentalOrganizationSettingsModel.update(organization => {
              organization.companyLogoUrl = response.fileUrl;
            });
          }
        }).catch(() => {
          enqueueSnackbar("Your file could not be uploaded. Please try again.", { variant: "error" });
        }).finally(() => {
          setSavingFile(false);
        })
      })
    },
    [rentalOrganizationSettingsModel]
  );

  const onRemoveFile = useCallback(() => {
    rentalOrganizationSettingsModel.update(organization => {
      organization.companyLogoUrl = "";
    })
  }, [rentalOrganizationSettingsModel]);

  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop, accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
    }
  })



  const [isOpenSaveChanges, setIsOpenSaveChanges] = useState(false);

  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const signatureCanvas = useRef<SignatureCanvas>(null);

  const saveDrawnSignature = async () => {
    if (!signatureCanvas.current || signatureCanvas.current.isEmpty()) {
      enqueueSnackbar("Please draw the signature first", { variant: "error" });
      return;
    }
    setSavingSignature(true);
    try {
      const fileUrl = await uploadSignatureCanvas(signatureCanvas.current);
      if (fileUrl) {
        rentalOrganizationSettingsModel.update((model) => {
          model.ownerSignatureFile = fileUrl;
        });
        setSignatureOpen(false);
      }
    } catch {
      enqueueSnackbar("The signature could not be saved. Please try again.", { variant: "error" });
    } finally {
      setSavingSignature(false);
    }
  };

  const updateRentalOrganizationSettings = useCallback(() => {
    rentalOrganizationSettingsModel.save().then((response) => {
      if (response) {
        // we should apply the response back to the load model
        updateData("rentalOrganizationSettings")(response.data as any)

        enqueueSnackbar("Successfully updated the Rental Organization Settings", { variant: "success" })
        setIsOpenSaveChanges(false);
      }
    })
  }, [rentalOrganizationSettingsModel, updateData]);


  const updateRentalOrganizationSettingsButton = <Button
    onClick={() => setIsOpenSaveChanges(true)}
    variant="outlined"
    color="info"
    startIcon={<Iconify icon="basil:save-solid" />}
  >
    Save Changes
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {rentalOrganizationSettingsModel.model && <Box sx={{ display: "flex", flexGrow: 1 }}>

          <Stack direction="column" flexGrow={1} gap={2}>
            <PageHeader title="Settings">
              {updateRentalOrganizationSettingsButton}
            </PageHeader>

            <Card>
              <CardHeader title="General" />

              <Stack spacing={2} sx={{ p: 2 }}>
                <TruxFormField label="Company Name *">
                  <TruxTextField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="companyName"
                  />
                </TruxFormField>

                <TruxFormField label="TimeZone *">
                  <TruxSelectField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="companyTimeZone"
                  >
                    {Constants.TIMEZONES.map((tz, i) => <MenuItem value={tz} key={i}>{tz}</MenuItem>)}
                  </TruxSelectField>
                </TruxFormField>

                <TruxFormField label="Outbound Opening Text">
                  <Stack>
                    <TextField
                      size="small"
                      multiline
                      minRows={4}
                      value={rentalOrganizationSettingsModel.model?.preImageText || ""}
                      onChange={(e) => rentalOrganizationSettingsModel.update((model) => {
                        model.preImageText = e.target.value;
                      })}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Printed on the outbound form before the inspection details
                    </Typography>
                  </Stack>
                </TruxFormField>

                <TruxFormField label="Outbound Closing Text">
                  <Stack>
                    <TextField
                      size="small"
                      multiline
                      minRows={4}
                      value={rentalOrganizationSettingsModel.model?.postImageText || ""}
                      onChange={(e) => rentalOrganizationSettingsModel.update((model) => {
                        model.postImageText = e.target.value;
                      })}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Closing clauses — printed after the inspection details, before the signature page
                    </Typography>
                  </Stack>
                </TruxFormField>

                <TruxFormField label="Contact Phone *">
                  <TruxTextField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="contactPhone"
                  />
                </TruxFormField>

                <TruxFormField label="Contact Email *">
                  <TruxTextField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="contactEmail"
                  />
                </TruxFormField>

                <TruxFormField label="Company Address *">
                  <Stack spacing={1}>
                    <Box display="flex" gap={2}>
                      <Box flexGrow={1}>
                        <TruxSimpleTextField label="Street 1 *" value={rentalOrganizationSettingsModel.model.companyAddress.street1} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.street1 = val;
                        })} />
                      </Box>
                      <Box flexGrow={1}>
                        <TruxSimpleTextField label="Street 2" value={rentalOrganizationSettingsModel.model.companyAddress.street2} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.street2 = val;
                        })} />
                      </Box>
                    </Box>
                    <Box display="flex" gap={2}>
                      <Box flexGrow={1}>
                        <TruxSimpleTextField label="City *" value={rentalOrganizationSettingsModel.model.companyAddress.city} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.city = val;
                        })} />
                      </Box>
                      <Box flexGrow={1}>
                        <TruxSimpleSelectField value={rentalOrganizationSettingsModel.model.companyAddress.state} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.state = val;
                        })}>
                          <MenuItem value="" selected={!!rentalOrganizationSettingsModel.model.companyAddress.state}>Please Select</MenuItem>
                          {Constants.STATES.map((state) => <MenuItem value={state} key={state} selected={state === rentalOrganizationSettingsModel?.model?.companyAddress.state}>{state}</MenuItem>)}
                        </TruxSimpleSelectField>
                      </Box>
                      <Box flexGrow={1}>
                        <TruxSimpleTextField label="Zip" value={rentalOrganizationSettingsModel.model.companyAddress.zip} onChange={(val) => rentalOrganizationSettingsModel.update((model) => {
                          model.companyAddress.zip = val;
                        })} />
                      </Box>
                    </Box>
                  </Stack>
                </TruxFormField>


                <TruxFormField label="Company Image">
                  {rentalOrganizationSettingsModel.model && <>
                    {savingFile && <CircularProgress />}
                    {!savingFile && rentalOrganizationSettingsModel.model.companyLogoUrl && <Stack direction="row" gap={2}>
                      <Box
                        component="img"
                        src={rentalOrganizationSettingsModel.model.companyLogoUrl}
                        alt="Company Logo"
                        sx={{
                          maxHeight: "72px",
                          maxWidth: "240px",
                          objectFit: "contain",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          p: 0.5,
                        }}
                      />
                      <Box>
                        <IconButton color="error" onClick={onRemoveFile} >
                          <Iconify icon="solar:trash-bin-trash-bold" />
                        </IconButton>
                      </Box>
                    </Stack>}
                    {!savingFile && !rentalOrganizationSettingsModel.model.companyLogoUrl && <Box>
                      <Box {...getRootProps()} onFocus={null as any} onBlur={null as any}>
                        <input {...getInputProps()} />

                        <Box sx={{
                          m: 0.5,
                          flexShrink: 0,
                          flexGrow: 1,
                          height: "80px",
                          width: "120px",
                          borderRadius: 1,
                          cursor: 'pointer',
                          alignItems: 'center',
                          color: 'text.disabled',
                          justifyContent: 'center',
                          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                          border: (theme) => `dashed 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                          ...(isDragActive && {
                            opacity: 0.72,
                          }),
                          '&:hover': {
                            opacity: 0.72,
                          }
                        }}><Stack spacing={0.5} paddingTop={1} alignItems="center">
                            <Iconify icon="eva:cloud-upload-fill" width={40} />
                            <Typography variant="body2">Upload File</Typography>
                          </Stack></Box>
                      </Box>
                    </Box>}
                  </>}
                </TruxFormField>

                <TruxFormField label="Owner Name">
                  <Stack>
                    <TruxTextField
                      modelSavingData={rentalOrganizationSettingsModel}
                      field="ownerName"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Printed in the Lessor block of every agreement sent for signing
                    </Typography>
                  </Stack>
                </TruxFormField>

                <TruxFormField label="Owner Signature">
                  <Stack gap={1}>
                    {rentalOrganizationSettingsModel.model.ownerSignatureFile && (
                      <Box
                        component="img"
                        src={rentalOrganizationSettingsModel.model.ownerSignatureFile}
                        alt="Owner Signature"
                        sx={{
                          maxHeight: "72px",
                          maxWidth: "240px",
                          objectFit: "contain",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          p: 0.5,
                          alignSelf: "flex-start",
                        }}
                      />
                    )}
                    <Stack direction="row" gap={1} alignItems="center">
                      <RentalFileField
                        fileUrl={rentalOrganizationSettingsModel.model.ownerSignatureFile}
                        onChange={(fileUrl) => rentalOrganizationSettingsModel.update((model) => {
                          model.ownerSignatureFile = fileUrl;
                        })}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Iconify icon="solar:pen-bold" width={16} />}
                        onClick={() => setSignatureOpen(true)}
                      >
                        Draw Signature
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Upload a photo or scan, or draw it here. Attached under the Lessor name on every agreement
                    </Typography>
                  </Stack>
                </TruxFormField>

                <TruxFormField label="Require Photos">
                  <TruxCheckboxField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="requirePhotos"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      All four sides of the trailer must be photographed on the Rent Out form
                    </Typography>
                  </Box>
                </TruxFormField>

                <TruxFormField label="Require Tire Brand">
                  <TruxCheckboxField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="requireTireBrand"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      The Brand must be recorded for every tire on the Rent Out form
                    </Typography>
                  </Box>
                </TruxFormField>

                <TruxFormField label="Require Tire Serial">
                  <TruxCheckboxField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="requireTireSerial"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      The Serial must be recorded for every tire on the Rent Out form
                    </Typography>
                  </Box>
                </TruxFormField>

                <TruxFormField label="Require Tire Condition">
                  <TruxCheckboxField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="requireTireCondition"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      The Condition must be recorded for every tire on the Rent Out form
                    </Typography>
                  </Box>
                </TruxFormField>

                <TruxFormField label="Require Brakes">
                  <TruxCheckboxField
                    modelSavingData={rentalOrganizationSettingsModel}
                    field="requireBrakes"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      The Condition must be recorded for every brake on the Rent Out form
                    </Typography>
                  </Box>
                </TruxFormField>

                <TruxFormField label="Tire Condition Type">
                  <Stack>
                    <TruxSelectField
                      modelSavingData={rentalOrganizationSettingsModel}
                      field="tireConditionType"
                    >
                      {RENTAL_TIRE_CONDITION_TYPES.map((type) => <MenuItem value={type.value} key={type.value}>{type.label}</MenuItem>)}
                    </TruxSelectField>
                    <Typography variant="caption" color="text.secondary">
                      How tire condition is recorded on the Rent Out form
                    </Typography>
                  </Stack>
                </TruxFormField>

              </Stack>
            </Card>
          </Stack>
        </Box>}
      </DisplayPageData >

      {
        rentalOrganizationSettingsModel.model &&
        <SimpleDialog
          title="Save Rental Organization Settings"
          open={isOpenSaveChanges}
          onCancel={() => {
            setIsOpenSaveChanges(false);
            rentalOrganizationSettingsModel.clearErrors();
          }}
          onSubmit={updateRentalOrganizationSettings}
          submitDisabled={rentalOrganizationSettingsModel.loading}
          submitButton="Save Organization"
          submitButtonIcon={<Iconify icon="basil:save-solid" />}
          submitButtonColor="info">

          <DisplayErrors savingData={rentalOrganizationSettingsModel} />

          Are you sure you would like to save your changes?
        </SimpleDialog>
      }

      <SimpleDialog
        title="Draw the Owner Signature"
        open={signatureOpen}
        onCancel={() => setSignatureOpen(false)}
        onSubmit={saveDrawnSignature}
        submitDisabled={savingSignature}
        submitButton="Use Signature"
        submitButtonIcon={<Iconify icon="solar:pen-bold" />}
        submitButtonColor="success"
        buttons={
          <Button variant="outlined" onClick={() => signatureCanvas.current?.clear()} disabled={savingSignature}>
            Clear
          </Button>
        }>
        <Stack gap={0.5}>
          <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 1, bgcolor: "common.white", alignSelf: "center" }}>
            <SignatureCanvas
              ref={signatureCanvas}
              penColor="black"
              canvasProps={{ width: 480, height: 200 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Sign with your finger or mouse
          </Typography>
        </Stack>
      </SimpleDialog>
    </Container >
  );
}
